"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Mail, MapPin, Phone, PhoneCall, User2, X } from "lucide-react"
import { type TripEnquiryFormSchema, tripEnquiryFormSchema } from "@/lib/validation"
import { useState, useTransition } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog"
import { tripEnquiryForm } from "@/server/actions/google-sheets-actions"
import { toast } from "sonner"
import LoadingButton from "../ui/loading-button"

const TripEnquiryForm = ({ destination }: { destination?: string }) => {
    const [isPending, startTransition] = useTransition()
    const [open, setOpen] = useState<boolean>(false)

    const form = useForm<TripEnquiryFormSchema>({
        resolver: zodResolver(tripEnquiryFormSchema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            destination: destination,
        },
    })

    function onSubmit(data: TripEnquiryFormSchema) {
        startTransition(async () => {
            const res = await tripEnquiryForm(data);
            if (!res.success) {
                toast.error(res.message)
                return;
            }

            toast.success(res.message)
            form.reset()
            setOpen(false)
        })
    }

    return (
        <Dialog open={open} onOpenChange={() => setOpen(true)}>
            <DialogTrigger>
                <button className='flex items-center justify-center py-2 lg:py-2.5 h-10 lg:h-[47px] w-11 lg:w-14 cursor-pointer hover:bg-custom-primary border border-custom-primary hover:border-custom-primary transition-all duration-300 group/btn rounded-md'>
                    <PhoneCall className='size-full shrink-0 fill-custom-primary group-hover/btn:fill-white transition duration-300' strokeWidth={0} />
                </button>
            </DialogTrigger>
            <DialogContent>
                <div className="relative">
                    <Button className="absolute -top-5 -right-5 z-30" size="icon" onClick={() => setOpen(false)}>
                        <X className="size-5" />
                    </Button>
                    <DialogHeader>
                        <DialogTitle className='text-3xl font-lexend font-semibold'>Fill out the form and our team will contact you soon....</DialogTitle>
                    </DialogHeader>
                    <div className='w-full mx-auto -mr-1'>
                        <div className="px-5 mt-4 rounded-lg shadow-[0px_4px_16px_4px_rgba(0,0,0,0.1)] md:p-0 md:shadow-none">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full max-w-md space-y-4 lg:space-y-6">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input placeholder="Enter Your Name" {...field} className="h-12 peer" />
                                                        <User2 className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input placeholder="Enter Your Email" {...field} className="h-12 peer" />
                                                        <Mail className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="phone"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input placeholder="Enter Phone Number" {...field} className="h-12 peer" />
                                                        <Phone className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="destination"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <div className="relative">
                                                        <Input placeholder="Enter Your Destination" {...field} className="h-12 peer" />
                                                        <MapPin className="h-5 w-5 absolute top-1/2 right-2.5 -translate-y-1/2 text-secondary-text peer-focus-visible:text-custom-primary" strokeWidth={1.8} />
                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <LoadingButton loading={isPending} type="submit" className="primary-btn bg-custom-primary w-full text-base">
                                        Submit Now
                                    </LoadingButton>
                                </form>
                            </Form>
                        </div>
                        <p className="font-medium text-red-600 font-lexend text-sm text-center mt-3">
                            **No spam, no unwanted calls, only personalised trip planning.
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

    )
}

export default TripEnquiryForm